import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { X, Check, Ban, Loader2, Search, CheckSquare, Square, AlertTriangle } from "lucide-react";
import api from "../lib/api";

const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  not_submitted: "Not Submitted",
};
const STATUS_API_PARAM = {
  pending: "pending",
  approved: "approved",
  rejected: "rejected",
  not_submitted: "not_submitted",
};

/**
 * Bulk Approve / Reject modal for Payroll Manpower Pay-Roster (Medical / Induction).
 * Triggered by clicking a count chip on the Pay-Roster Status Breakdown strip.
 * Fetches /api/pay-roster?status={key} on open. Lets admins multi-select rows
 * (or Select All) and dispatch /api/pay-roster/bulk-approve or /bulk-reject in
 * one call. Result counts are surfaced via Sonner toast and the parent's
 * onCompleted() is fired so the report can refresh.
 */
export default function PayRosterBulkModal({ open, statusKey, onClose, onCompleted }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [rejectReasonOpen, setRejectReasonOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  useEffect(() => {
    if (!open || !statusKey) return;
    setLoading(true);
    setSelected(new Set());
    setQ("");
    api.get("/pay-roster", { params: { status: STATUS_API_PARAM[statusKey] } })
      .then((r) => setRows(r.data || []))
      .catch((e) => toast.error(e?.response?.data?.detail || "Could not load pay-roster records"))
      .finally(() => setLoading(false));
  }, [open, statusKey]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((c) =>
      [c.name, c.contact_phone, c.ncc_site_name, c.id_proof_number, (c.trades || []).join(" ")]
        .filter(Boolean).join(" ").toLowerCase().includes(s));
  }, [rows, q]);

  const allVisibleSelected = filtered.length > 0 && filtered.every((c) => selected.has(c.id));
  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleAllVisible() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) filtered.forEach((c) => next.delete(c.id));
      else filtered.forEach((c) => next.add(c.id));
      return next;
    });
  }

  async function runApprove() {
    if (selected.size === 0) return toast.error("No rows selected");
    if (!window.confirm(`Approve ${selected.size} Pay-Roster record(s)? This unlocks Form-XI / Screening / ID Card print for each.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/pay-roster/bulk-approve", { ids: [...selected] });
      toast.success(`Approved ${r.data.approved} · skipped ${r.data.skipped}`);
      onCompleted?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk approve failed");
    } finally { setBusy(false); }
  }

  async function runReject() {
    if (selected.size === 0) return toast.error("No rows selected");
    setBusy(true);
    try {
      const r = await api.post("/pay-roster/bulk-reject", { ids: [...selected], reason: rejectReason.trim() || null });
      toast.success(`Rejected ${r.data.rejected} · skipped ${r.data.skipped}`);
      onCompleted?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bulk reject failed");
    } finally {
      setBusy(false);
      setRejectReasonOpen(false);
      setRejectReason("");
    }
  }

  if (!open) return null;

  const headingColor = statusKey === "approved" ? "bg-emerald-700" : statusKey === "rejected" ? "bg-red-700" : statusKey === "pending" ? "bg-amber-600" : "bg-slate-700";

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/60 p-4 print:hidden" onClick={onClose} data-testid="pay-roster-bulk-modal">
      <div className="flex max-h-[88vh] w-full max-w-4xl flex-col border-2 border-slate-900 bg-white shadow-brutal-lg" onClick={(e) => e.stopPropagation()}>
        <header className={`flex items-start justify-between border-b-2 border-slate-900 ${headingColor} px-4 py-3 text-white`}>
          <div>
            <div className="industrial-label text-[10px] text-white/80">// Bulk Pay-Roster Action</div>
            <h2 className="mt-0.5 font-heading text-xl font-extrabold">
              {STATUS_LABEL[statusKey] || "Pay-Roster"} · {rows.length} record(s)
            </h2>
            <div className="mt-1 font-mono text-[11px] text-white/80">
              {selected.size} selected · {filtered.length} visible after search
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="pay-roster-bulk-close" className="rounded-md p-1 hover:bg-white/10">
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className="flex items-center gap-2 border-b-2 border-slate-900 bg-slate-50 px-4 py-2">
          <button
            type="button"
            onClick={toggleAllVisible}
            data-testid="pay-roster-bulk-toggle-all"
            className="inline-flex items-center gap-1.5 border-2 border-ncc-navy bg-white px-2.5 py-1 industrial-label text-[11px] hover:bg-slate-50"
          >
            {allVisibleSelected ? <CheckSquare className="h-3.5 w-3.5" /> : <Square className="h-3.5 w-3.5" />}
            {allVisibleSelected ? "Clear visible" : "Select visible"}
          </button>
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, mobile, site…"
              data-testid="pay-roster-bulk-search"
              className="w-full border-2 border-slate-300 bg-white py-1 pl-7 pr-2 font-mono text-[12px] focus:border-slate-900 focus:outline-none"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="grid place-items-center py-16 text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : filtered.length === 0 ? (
            <div className="grid place-items-center py-16 text-slate-500 font-mono text-sm">
              No records match this filter.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-100 text-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left industrial-label w-10"></th>
                  <th className="px-3 py-2 text-left industrial-label">Name</th>
                  <th className="px-3 py-2 text-left industrial-label">Mobile</th>
                  <th className="px-3 py-2 text-left industrial-label">NCC Site</th>
                  <th className="px-3 py-2 text-left industrial-label">Trade</th>
                  <th className="px-3 py-2 text-left industrial-label">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => {
                  const isSel = selected.has(c.id);
                  return (
                    <tr
                      key={c.id}
                      onClick={() => toggle(c.id)}
                      data-testid={`pay-roster-bulk-row-${c.id}`}
                      className={`cursor-pointer border-t border-slate-200 ${isSel ? "bg-emerald-50" : "hover:bg-slate-50"}`}
                    >
                      <td className="px-3 py-2">
                        {isSel ? <CheckSquare className="h-4 w-4 text-emerald-700" /> : <Square className="h-4 w-4 text-slate-400" />}
                      </td>
                      <td className="px-3 py-2 font-bold text-slate-900">{c.name || "—"}</td>
                      <td className="px-3 py-2 font-mono">{c.contact_phone || "—"}</td>
                      <td className="px-3 py-2">{c.ncc_site_name || <span className="text-amber-600">No site</span>}</td>
                      <td className="px-3 py-2">{(c.trades && c.trades[0]) || "—"}</td>
                      <td className="px-3 py-2 font-mono text-[11px] text-slate-600">
                        {c.pay_roster_submitted_at ? new Date(c.pay_roster_submitted_at).toLocaleDateString("en-GB") : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <footer className="flex items-center justify-between gap-2 border-t-2 border-slate-900 bg-slate-50 px-4 py-3">
          <div className="font-mono text-[11px] text-slate-600 flex items-center gap-1">
            {statusKey === "not_submitted" && (
              <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" /> Bulk-approving &quot;Not Submitted&quot; bypasses the contractor&apos;s submission step.</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setRejectReasonOpen(true)}
              disabled={selected.size === 0 || busy}
              data-testid="pay-roster-bulk-reject"
              className="inline-flex items-center gap-1.5 border-2 border-red-600 bg-white px-3 py-1.5 industrial-label text-[11px] text-red-700 hover:bg-red-50 disabled:opacity-40"
            >
              <Ban className="h-3.5 w-3.5" /> Bulk Reject
            </button>
            <button
              type="button"
              onClick={runApprove}
              disabled={selected.size === 0 || busy}
              data-testid="pay-roster-bulk-approve"
              className="inline-flex items-center gap-1.5 border-2 border-emerald-700 bg-emerald-700 px-3 py-1.5 industrial-label text-[11px] text-white hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-40"
            >
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Bulk Approve ({selected.size})
            </button>
          </div>
        </footer>
      </div>

      {/* Reject reason sub-modal */}
      {rejectReasonOpen && (
        <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/70 p-4" onClick={() => setRejectReasonOpen(false)}>
          <div className="w-full max-w-md border-2 border-slate-900 bg-white shadow-brutal-lg" onClick={(e) => e.stopPropagation()} data-testid="pay-roster-bulk-reject-modal">
            <div className="border-b-2 border-slate-900 bg-red-600 px-4 py-3 text-white">
              <div className="industrial-label">Bulk Reject · {selected.size} record(s)</div>
            </div>
            <div className="p-4 space-y-2">
              <div className="font-mono text-xs text-slate-600">Reason (shared across all rejected records, stored in audit log):</div>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                data-testid="pay-roster-bulk-reject-reason"
                className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                placeholder="e.g. Missing Form-XI fields, photograph unclear"
              />
            </div>
            <div className="flex items-center justify-end gap-2 border-t-2 border-slate-900 bg-slate-50 px-4 py-3">
              <button type="button" onClick={() => setRejectReasonOpen(false)} className="border-2 border-slate-900 bg-white px-3 py-1.5 industrial-label text-[11px]">Cancel</button>
              <button type="button" onClick={runReject} disabled={busy} data-testid="pay-roster-bulk-reject-confirm" className="border-2 border-red-600 bg-red-600 px-3 py-1.5 industrial-label text-[11px] text-white disabled:opacity-50">
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : "Confirm Reject"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
